import React from 'react';

import { Box, Typography } from '@mui/material';
import {
  AppointmentRecurring, PartnerReferralActivity, PatientActivity, PatientActivityType,
  PatientDocumentType,
  PatientMatchActivity
} from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';

import { ReviewLayoutContent } from '../../../../../Layout';
import { LightChip, LightChipProps } from '../../../../../LightChip';
import {
  AssessmentExpiredView, AssessmentInitialView, AssessmentView, FormInitialView
} from '../../../assessment-submissions-review-modal/AssessmentView.component';
import { getSessionTitle } from '../helpers';
import { documentsMapping, PatientStaticDocumentComponent } from '../PatientStaticDocument';
import { PatientUploadedDocumentReview } from '../PatientUploadedDocument';
import { AddendaTitleChip, SessionClinicalNotesReview } from '../SessionClinicalNotesReview.component';
import { FormResponsesReviewTable } from './FormResponsesReviewTable';
import { BaseMedicalNoteReview } from './MedicalNotes/BaseMedicalNote';
import { PartnerReferralDetails } from './StudentReferral';
import { PsychiatryReferralDetails } from './StudentReferral/PsychiatryReferralDetails.component';
import { CancelledAppointmentDetails } from './CancelledAppointmentDetails.component';

export type ActivityDetailedViewProps = {
  activityItem: PatientActivity;
};


const TitleWithChip: React.VFC<{ title: string; chip: string; chipColor?: LightChipProps['color'] }> = (props) => {
  return (
    <>{props.title} <LightChip size="small" color={props.chipColor} label={props.chip} style={{ fontWeight: 400 }} /></>
  );
};


export const ActivityDetailedView: React.FC<ActivityDetailedViewProps> = ({ activityItem }) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const l10n = useL10n();

  switch (activityItem.activityType) {
    case PatientActivityType.document:
      switch (activityItem.document.type) {
        case PatientDocumentType.static:
          return (
            <ReviewLayoutContent title={documentsMapping[activityItem.document.documentKey].title} subtitle="sent by Uplift">
              <PatientStaticDocumentComponent
                document={activityItem.document}
                acknowledgedAt={CalendarDateService.date(activityItem.datetime)}
              />
            </ReviewLayoutContent>
          );
        case PatientDocumentType.medicalRecord:
          return (
            <React.Suspense fallback={null}>
              <PatientUploadedDocumentReview
                patientId={activityItem.document.patientId}
                medicalDocumentId={activityItem.document.id}
                editable={me.id === activityItem.document.providerId}
                showControls={true}
              />
            </React.Suspense>
          );
      }

      return null;
    case PatientActivityType.assessmentFormInitial:
      return (
        <ReviewLayoutContent title={<TitleWithChip title={activityItem.form.name} chip="Incomplete" chipColor="warning" />} subtitle="sent by Uplift">
          <AssessmentInitialView patientId={activityItem.submission.userId} />
        </ReviewLayoutContent>
      );
    case PatientActivityType.assessmentFormCompleted:
      return (
        <ReviewLayoutContent title={activityItem.form.name} subtitle="sent by Uplift">
          <AssessmentView
            submission={activityItem.submission}
            assessmentKey={activityItem.formKey}
            score={activityItem.score}
            percentage={activityItem.scoreDiffPercentage}
          />
        </ReviewLayoutContent>
      );
    case PatientActivityType.assessmentFormExpired:
      return (
        <ReviewLayoutContent
          title={<TitleWithChip title={activityItem.form.name} chip={`Expired ${CalendarDateService.format(CalendarDateService.date(activityItem.submission.expiredAt), 'MMM D', 'MMM D, YYYY', { timezone })}`} />}
          subtitle="sent by Uplift"
        >
          <AssessmentExpiredView patientId={activityItem.submission.userId} />
        </ReviewLayoutContent>
      );
    case PatientActivityType.formInitial:
      return (
        <ReviewLayoutContent title={<TitleWithChip title={activityItem.form.name} chip="Incomplete" chipColor="warning" />} subtitle="sent by Uplift">
          <FormInitialView patientId={activityItem.submission.userId} />
        </ReviewLayoutContent>
      );
    case PatientActivityType.formExpired:
    case PatientActivityType.formCompleted:
      return (
        <ReviewLayoutContent title={activityItem.form.name} subtitle="sent by Uplift">
          <FormResponsesReviewTable
            submissionId={activityItem.submission.formSubmissionId}
            patientId={activityItem.submission.userId}
            submittedAt={activityItem.submission.completedAt}
          />
        </ReviewLayoutContent>
      );
    case PatientActivityType.therapySessionCompleted:
    case PatientActivityType.psychiatrySessionCompleted:
      return (
        <ReviewLayoutContent
          title={(
            <Box display="flex" gap="8px">
              {getSessionTitle(l10n, activityItem.appointment, activityItem.isFirstSession)}
              {activityItem.appointment.recurring
                && activityItem.appointment.recurring !== AppointmentRecurring.none
                && <LightChip label={l10n.appointments.recurring[activityItem.appointment.recurring]} size="small" color="secondary" />
              }
            </Box>
          )}
          subtitle={`with ${activityItem.provider.firstName[0]}. ${activityItem.provider.lastName}`}
        >
          <Typography>{activityItem.provider.firstName} {activityItem.provider.lastName} has not submitted a note for this session</Typography>
        </ReviewLayoutContent>
      );
    case PatientActivityType.therapySessionSubmitted: {
      return (
        <ReviewLayoutContent
          title={(
            <Box display="flex" gap="8px">
              {getSessionTitle(l10n, activityItem.appointment, activityItem.isFirstSession)}
              <AddendaTitleChip sessionId={activityItem.clinicalNotes.sessionId} />
            </Box>
          )}
          subtitle={`with ${activityItem.provider.firstName[0]}. ${activityItem.provider.lastName}`}
        >
          <SessionClinicalNotesReview
            appointmentId={activityItem.appointment.id}
            patientId={activityItem.appointment.patientId}
          />
        </ReviewLayoutContent>
      );
    }
    case PatientActivityType.psychiatrySessionSubmitted:
      return (
        <ReviewLayoutContent
          title={(
            <Box display="flex" gap="8px">
              {getSessionTitle(l10n, activityItem.appointment, activityItem.isFirstSession)}
              {activityItem.appointment.recurring
                && activityItem.appointment.recurring !== AppointmentRecurring.none
                && <LightChip label={l10n.appointments.recurring[activityItem.appointment.recurring]} size="small" color="secondary" />
              }
            </Box>
          )}
          subtitle={`with ${activityItem.provider.firstName[0]}. ${activityItem.provider.lastName}`}
        >
          <SessionClinicalNotesReview
            appointmentId={activityItem.appointment.id}
            patientId={activityItem.appointment.patientId}
          />
        </ReviewLayoutContent>
      );
    case PatientActivityType.noteSubmitted:
      return <BaseMedicalNoteReview activityItem={activityItem} />;
    // case PatientActivityType.therapySessionInitial:
    //   return (
    //     <ReviewLayoutContent title={`${activityItem.activityType} | ${activityItem.appointment.type}`} sentBy="Uplift">
    //       {activityItem.appointment.state}
    //     </ReviewLayoutContent>
    //   );
    // case PatientActivityType.psychiatrySessionInitial:
    //   return (
    //     <ReviewLayoutContent title={`${activityItem.activityType} | ${activityItem.appointment.type}`} sentBy="Uplift">
    //       {activityItem.appointment.state}
    //     </ReviewLayoutContent>
    //   );
    case PatientActivityType.therapySessionCancelled:
    case PatientActivityType.psychiatrySessionCancelled:
      return (
        <ReviewLayoutContent title="Cancelled Session" subtitle={`with ${activityItem.provider.firstName[0]}. ${activityItem.provider.lastName}`}>
          <CancelledAppointmentDetails appointment={activityItem.appointment} />
        </ReviewLayoutContent>
      );
    case PatientActivityType.referral:
      return (
        <ReviewLayoutContent title="Partner Referral" subtitle={`from ${activityItem.aboutUser?.profile?.preferredName ?? activityItem.aboutUser?.user?.firstName}'s school`}>
          <PartnerReferralDetails activityItem={activityItem as PartnerReferralActivity} />
        </ReviewLayoutContent>
      );
    case PatientActivityType.newMatchByReferral:
      return (
        <PsychiatryReferralDetails
          activityItem={activityItem as PatientMatchActivity}
        />
      );

    default:
      // TODO: add more views, for forms and assessments
  }


  return (
    <ReviewLayoutContent title={activityItem.activityType} subtitle="sent by Uplift">
      {activityItem.activityType}
    </ReviewLayoutContent>
  );
};
